import {
  FormatAsCurrencyForLocale,
  DateToDateAndTimeFormat
} from '@PetSureAustralia/petsure-react-ui-library/distLib/helpers';
import { PETSURE_UNDERWRITER, HOLLARD_UNDERWRITER } from '../constants/constants';
import { PAYMENT_ERRROR_MESSAGE_MAPPING, PAYMENT_RESPONSE_CODE } from '../constants/paymentErrorMessage';

// Check underwriter value on policy level
export const isPetsureUnderwriter = ({ petDetails, policyNumber }) => {
  return petDetails?.some((pet) => pet.PolicyNumber === policyNumber && pet.Underwriter === PETSURE_UNDERWRITER);
};

export const showSelectAllPetCheckbox = (petDetails) => {
  return (
    petDetails?.every((pet) => pet.Underwriter === PETSURE_UNDERWRITER) ||
    petDetails?.every((pet) => pet.Underwriter === HOLLARD_UNDERWRITER)
  );
};

// Transform legacy policy number to be able to use it in GET Method
export const legacyPolicyNumberHandler = (policyNumber) => {
  if (!policyNumber.includes('/')) return policyNumber;
  return policyNumber.replaceAll('/', '_');
};

export const revertLegacyPolicyNumberFormat = (policyNumber) => {
  if (!policyNumber) return '';
  if (!policyNumber.includes('_')) return policyNumber;
  return policyNumber.replaceAll('_', '/');
};

export const disableNextButtonHandler = (petIndex, totalAmount) => {
  return totalAmount ? !totalAmount[petIndex] || totalAmount[petIndex] === 0 : true;
};

export const totalAmountHandler = (arr) => {
  const totalDueAmoount =
    arr.filter((instalment) => instalment.isSelected).reduce((total, instalment) => total + instalment.DueAmount, 0) ||
    0;
  return Number(totalDueAmoount.toFixed(2));
};

export const initWebCollectionErrorMessage = (message) => {
  if (!message) return null;

  if (message.includes('has already been requested for web collection')) {
    return 'We are unable to process your payment at this time. Please try again later.';
  }
  if (message.includes('is not in Initial status') || message.includes('is not eligible for web collection')) {
    return 'We are unable to process your payment at this time. Please contact our customer service team to make this payment.';
  }
  if (message.includes('Web collection not allowed at this time') || message.includes('does not exist')) {
    return 'We are unable to process your payment at this time. Please contact our customer service team to discuss this installment.';
  }
  return null;
};

// Initialized instalment record to be processed in real time payment
export const initInstalments = (instalmentToPay, initWebCollection) => {
  if (!instalmentToPay) return [];

  const delay = (ms) =>
    new Promise((resolve) => {
      setTimeout(resolve, ms);
    });

  const initiateInstalments = async () => {
    const response = await Promise.all(
      instalmentToPay?.map((item, index) => {
        if (item.isSelected && item.isValid) {
          return delay(index * 1000).then(async () => {
            const payload = {
              Amount: item.DueAmount,
              CustomerId: item.CustomerId,
              LineNumber: item.InstalmentLineNumber,
              PolicyNumber: item.PolicyNumber
            };

            try {
              const response = await initWebCollection(payload);
              return {
                RequestId: response?.data?.responseObject ?? '',
                CollectedAmount: item.DueAmount,
                InstalmentNumber: item.InstalmentNumber,
                LineNumber: item.InstalmentLineNumber,
                ErrorMessage: response?.data?.message ?? ''
              };
            } catch (error) {
              return null;
            }
          });
        }
        return Promise.resolve(null);
      })
    );

    return response.filter(Boolean); // Filter out null values
  };
  return initiateInstalments();
};

// Update instalment status to rejected if there atleast on of the selected insalment fails the initialization
export const hasOneFailedInit = (initRequestIds, updateInstalmentInUpm) => {
  const filterRequestIds = initRequestIds.filter((item) => item.RequestId !== 0);
  // Update instalment to rejected
  filterRequestIds?.map(async (item) => {
    const payload = {
      CollectedAmount: 0,
      Comment: 'Cancelled Payment from Customer Service Portal',
      ProcessDatetime: new Date(),
      ReferenceNumber: '',
      RequestId: item.RequestId,
      Success: false
    };
    try {
      await updateInstalmentInUpm(payload);
    } catch (err) {
      // Do nothing
      // if it fails it will be handled by validateOutstandingPayment in selectPaymentPanel.jsx
      // the instalment status won't be updated, thereforce it will be invalid on select payment step
      throw Error(err);
    }
  });
};

// Update instalment status based on the response from takePayment
export const updateUpmInstalmentRecord = (requestIds, updateInstalmentInUpm, paymentResponse) => {
  if (paymentResponse && paymentResponse.ReceiptNumber) {
    if (paymentResponse?.SummaryCode === '0') {
      requestIds?.map(async (item) => {
        const payload = {
          CollectedAmount: item.CollectedAmount,
          Comment: `Successful payment from customer service portal. Payment Ref Num: ${
            paymentResponse.ReceiptNumber
          }, Date: ${DateToDateAndTimeFormat(
            new Date(paymentResponse.TransactionTime)
          )}, Amount: ${FormatAsCurrencyForLocale(item.CollectedAmount)}, Instalment number: ${item.InstalmentNumber}`,
          ProcessDatetime: new Date(),
          ReferenceNumber: paymentResponse.ReceiptNumber,
          RequestId: item.RequestId,
          Success: true
        };
        try {
          await updateInstalmentInUpm(payload);
        } catch (err) {
          // Do nothing
          // if it fails it will be handled by validateOutstandingPayment in selectPaymentPanel.jsx
          // the instalment status won't be updated, thereforce it will be invalid on select payment step
          throw Error(err);
        }
      });
    } else {
      requestIds?.map(async (item) => {
        const payload = {
          CollectedAmount: item.CollectedAmount,
          Comment: `Payment unsuccessful from customer service portal. Reason: ${
            paymentResponse?.ResponseDescription
          }. Payment Ref Num: ${paymentResponse?.ReceiptNumber}, Date: ${DateToDateAndTimeFormat(
            new Date(paymentResponse?.TransactionTime)
          )}, Amount: ${FormatAsCurrencyForLocale(item.CollectedAmount)}, Instalment Number: ${item.InstalmentNumber}`,
          ProcessDatetime: new Date(),
          ReferenceNumber: paymentResponse.ReceiptNumber,
          RequestId: item.RequestId,
          Success: false
        };
        try {
          await updateInstalmentInUpm(payload);
        } catch (err) {
          // Do nothing
          // if it fails it will be handled by validateOutstandingPayment in selectPaymentPanel.jsx
          // the instalment status won't be updated, thereforce it will be invalid on select payment step
          throw Error(err);
        }
      });
    }
  }
};

export const getPaymentErrorMessageMapping = (responseCode) => {
  switch (responseCode) {
    case PAYMENT_RESPONSE_CODE.NOT_SUFFICIENT_FUNDS:
      return PAYMENT_ERRROR_MESSAGE_MAPPING.NOT_SUFFICIENT_FUNDS;

    case PAYMENT_RESPONSE_CODE.CARD_EXPIRED:
      return PAYMENT_ERRROR_MESSAGE_MAPPING.CARD_EXPIRED;

    case PAYMENT_RESPONSE_CODE.CARD_NOT_VALID:
      return PAYMENT_ERRROR_MESSAGE_MAPPING.CARD_NOT_VALID;

    case PAYMENT_RESPONSE_CODE.ACCOUNT_CLOSED:
      return PAYMENT_ERRROR_MESSAGE_MAPPING.ACCOUNT_CLOSED;

    default:
      if (PAYMENT_RESPONSE_CODE.BANK_REJECTED.includes(responseCode)) {
        return PAYMENT_ERRROR_MESSAGE_MAPPING.BANK_REJECTED;
      }
      return PAYMENT_ERRROR_MESSAGE_MAPPING.DEFAULT;
  }
};
